/**
 * useWidgetData - Простой хук для загрузки данных виджетов
 * ==========================================================
 *
 * Загружает данные один раз при монтировании и при изменении фильтров.
 *
 * Контракт fetch-функции виджета:
 *   fetchFunction(filters, options) -> Promise<data>
 * где options = { signal: AbortSignal, force?: boolean }.
 * Второй аргумент опционален: фетчер может его игнорировать, но тогда его
 * HTTP-запрос не будет отменяться при смене фильтров.
 *
 * Отмена (AbortError) — ожидаемый control flow: состояние виджета не трогаем,
 * ошибку не показываем, loading оставляем включённым до следующего ответа.
 */

import { useState, useEffect, useRef } from 'react';
import { useFilters } from './useFilters';
import { IS_DEBUG, describePayload } from '../utils/debug';
import { isAbortError } from '../services/api';

export function useWidgetData(fetchFunction, dependencies = []) {
  const { filters } = useFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchFunctionRef = useRef(fetchFunction);
  const abortRef = useRef(null);

  // Обновляем ref при изменении функции
  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
  });

  // Прерываем последний запрос при размонтировании
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Прерывает предыдущий запрос и заводит контроллер для нового
  const startRequest = () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    return controller;
  };

  useEffect(() => {
    let cancelled = false;
    const controller = startRequest();

    const loadData = async () => {
      setLoading(true);
      setError(null);

      // Метка для замера времени загрузки = имя fetch-функции виджета
      // (fallback 'widget', если функция анонимная).
      const label = fetchFunctionRef.current?.name || 'widget';
      const t0 = performance.now();

      try {
        const result = await fetchFunctionRef.current(filters, {
          signal: controller.signal
        });

        if (IS_DEBUG) {
          const elapsed = Math.round(performance.now() - t0);
          console.log(`⏱️ [widget] ${label}: ${elapsed}ms →`, describePayload(result));
        }

        if (!cancelled && !controller.signal.aborted) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        // Отменённый запрос не является ошибкой виджета
        if (isAbortError(err) || controller.signal.aborted) {
          return;
        }
        if (!cancelled) {
          console.error(`[widget] ${label}: ${err?.message || err}`);
          setError(err.message || 'Failed to load data');
          setData(null);
        }
      } finally {
        if (!cancelled && !controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, ...dependencies]); // Загружаем при изменении фильтров или зависимостей

  // Функция для ручного обновления (кнопка Retry в BaseWidget)
  const refetch = async () => {
    const controller = startRequest();
    setLoading(true);
    setError(null);

    try {
      // force — мимо клиентского TTL-кэша, иначе Retry вернул бы то же самое
      const result = await fetchFunctionRef.current(filters, {
        signal: controller.signal,
        force: true
      });
      if (controller.signal.aborted) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (isAbortError(err) || controller.signal.aborted) {
        return;
      }
      console.error(`[widget] refetch: ${err?.message || err}`);
      setError(err.message || 'Failed to load data');
      setData(null);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  return {
    data,
    loading,
    error,
    refetch,
    isLoading: loading,
    isError: !!error,
    isSuccess: !loading && !error && data !== null
  };
}


// Экспортируем по умолчанию основной хук
export default useWidgetData;
